import React from 'react';

import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { BUBBLE_CORNER, BUBBLE_RADIUS, paneTokens } from '../uiStream/paneTokens';

type AnswerCardProps = {
  children: React.ReactNode;
  receipt?: React.ReactNode;
  framed?: boolean;
  /**
   * 'bubble': askRichard's turn on the thread. Compass White with a hairline Ocean Teal edge and
   * one sharp corner at the avatar; the receipt is the bubble's footer. Default keeps the legacy
   * card for the older chat path.
   */
  variant?: 'card' | 'bubble';
};

/**
 * A settled answer is one artefact: its explanation, visual result and trust receipt belong
 * together. Keep the only edge on the outside so it never reads like a stack of widgets.
 */
export default function AnswerCard({ children, receipt, variant = 'card', framed = true }: AnswerCardProps) {
  const theme = useTheme();
  const pane = paneTokens(theme);
  const bubble = variant === 'bubble';
  return (
    <Box
      data-testid={framed ? 'settled-answer-card' : undefined}
      data-variant={framed ? variant : undefined}
      sx={
        framed
          ? {
              overflow: 'hidden',
              border: '1px solid',
              borderColor: bubble ? pane.hairline : theme.palette.divider,
              borderRadius: bubble ? `${BUBBLE_CORNER}px ${BUBBLE_RADIUS}px ${BUBBLE_RADIUS}px ${BUBBLE_RADIUS}px` : 1,
              bgcolor: bubble ? pane.assistantBubble : 'background.paper',
              boxShadow: bubble || theme.palette.mode === 'dark' ? 'none' : '0 1px 2px rgba(28,31,38,0.06)',
            }
          : { display: 'contents' }
      }
    >
      <Box sx={framed ? { px: bubble ? 1.75 : { xs: 1.25, sm: 1.5 }, pt: 1.25, pb: 1 } : { display: 'contents' }}>
        {children}
      </Box>
      {receipt ? (
        <Box
          sx={{
            px: bubble ? 1.75 : { xs: 1.25, sm: 1.5 },
            py: 1,
            borderTop: '1px solid',
            borderColor: bubble ? pane.hairline : 'divider',
            bgcolor: bubble ? 'transparent' : theme.palette.mode === 'dark' ? theme.palette.action.hover : 'rgba(28,31,38,0.025)',
          }}
        >
          {receipt}
        </Box>
      ) : null}
    </Box>
  );
}
